import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, MinusIcon } from 'lucide-react';
type FaqItem = {
  question: string;
  answer: string | string[];
};
const faqs: FaqItem[] = [{
  question: 'Will servicing my Toyota at Lancer void my warranty?',
  answer: "No. Under the Magnuson-Moss Warranty Act — a federal law enforced by the FTC — a manufacturer cannot void your warranty simply because you had your vehicle serviced at an independent shop. As long as the work meets manufacturer specifications and you keep your service records, your warranty stays intact. If you have questions about your specific situation, we're happy to talk it through."
}, {
  question: 'Do you work on Toyota hybrids and EVs?',
  answer: 'Yes. We regularly service the full Toyota hybrid lineup — Prius, Prius Prime, RAV4 Hybrid, RAV4 Prime, Camry Hybrid, Highlander Hybrid, Venza Hybrid, and Sienna Hybrid — as well as Toyota EVs. Routine maintenance, warning lights, hybrid system inspections, electrical diagnostics — bring it in.'
}, {
  question: 'My Toyota has over 150,000 miles. Is it worth a full inspection?',
  answer: 'Especially then. High-mileage Toyotas are often in better shape than people expect — but they do have specific wear patterns and components worth watching proactively. Most of our high-mileage Toyota customers leave with either peace of mind or a concrete plan. Usually both.'
}, {
  question: "What's a Digital Vehicle Inspection?",
  answer: "It's a full inspection report sent to your phone — photos and video of your actual vehicle, technician notes in plain English, and a prioritized list of what needs attention now versus what can wait. Everything is documented before any work is approved. Most customers tell us it's the first time they've actually seen what's going on with their car."
}, {
  question: 'Do I have to use OEM Toyota parts?',
  answer: "Not necessarily. We use quality parts that meet or exceed manufacturer specifications. If you have a preference for OEM parts on a specific repair, just let us know — we're happy to talk through what makes sense for your car and your budget."
}, {
  question: 'How is Lancer different from a general shop?',
  answer: ['Most shops can change your oil and rotate your tires. Fewer know that a high-mileage Tacoma has different wear patterns than a newer RAV4 Hybrid, or what tends to develop on a Prius at 130,000 miles before it gets expensive.', "We've been working on Toyotas and Lexus vehicles long enough to know how these platforms age — where they're strong, where to look, and what's actually worth fixing versus what can wait. We also handle Prius battery diagnostics and repair in-house, which most independent shops don't offer.", 'Beyond Toyota familiarity, every visit includes a full Digital Vehicle Inspection with photos and video of your actual car, plain-English notes, and a prioritized estimate. Nothing gets approved without your go-ahead. We work with both OEM and aftermarket diagnostic tools, so there are no gaps in what we can find or fix.', "If you've had a dealer experience that didn't feel right, or you're just looking for a shop that will treat your Toyota like the investment it is — that's exactly who we're here for."]
}];
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="py-20 px-4 bg-brand-light" data-id="element-46">
      <div className="max-w-4xl mx-auto" data-id="element-47">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-48">
          <span className="uppercase tracking-widest text-sm font-bold text-brand-accent mb-4 block" data-id="element-49">
            Common questions
          </span>
          <h2 className="font-heading uppercase tracking-wide text-3xl md:text-4xl lg:text-5xl font-extrabold text-brand-dark" data-id="element-50">
            Things Toyota owners usually ask us
          </h2>
        </motion.div>

        <div className="space-y-4" data-id="element-51">
          {faqs.map((faq, index) => {
          const isOpen = openIndex === index;
          const paragraphs = Array.isArray(faq.answer) ? faq.answer : [faq.answer];
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-52">
                <button type="button" onClick={() => setOpenIndex(isOpen ? null : index)} className="w-full flex items-center justify-between gap-4 text-left p-6 md:p-7 hover:bg-gray-50 transition-colors" aria-expanded={isOpen} data-id="element-53">
                  <span className="font-heading uppercase tracking-wide text-base md:text-lg font-bold text-brand-dark" data-id="element-54">
                    {faq.question}
                  </span>
                  <span className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-brand-accent text-white' : 'bg-brand-light text-brand-dark'}`} data-id="element-55">
                    {isOpen ? <MinusIcon className="w-5 h-5" data-id="element-56" /> : <PlusIcon className="w-5 h-5" data-id="element-57" />}
                  </span>
                </button>
                <AnimatePresence initial={false} data-id="element-58">
                  {isOpen && <motion.div key="content" initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-59">
                      <div className="px-6 md:px-7 pb-6 md:pb-7 -mt-1 space-y-4" data-id="element-60">
                        {paragraphs.map((paragraph, pIndex) => <p key={pIndex} className="text-lg text-gray-700 leading-relaxed" data-id="element-61">
                            {paragraph}
                          </p>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}